/**
 * debugLogger.ts
 * AI 写作助手调试日志系统 - v1.0
 *
 * 四个维度的日志记录，随调试模式开关同步：
 * - AI 行为：API 调用请求/响应、流式内容、思考过程
 * - 工具行为：工具调用参数、执行结果、错误信息
 * - 系统行为：Agent 循环迭代、状态变更、错误处理
 * - 用户行为：用户输入、开关操作、中断操作
 *
 * 注：错误/警告级别的 AI 调试事件会转发到统一日志（utils/logger），
 * 无论调试模式是否开启，都会上报云端，便于线上排障。
 */

import { logger } from './logger';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

type LogCategory = 'AI' | 'TOOL' | 'SYSTEM' | 'USER';

interface LogEntry {
  timestamp: string;
  category: LogCategory;
  level: LogLevel;
  event: string;
  data?: unknown;
  elapsed: number;
}

class DebugLogger {
  private enabled = false;
  private sessionStart = 0;

  constructor() {
    this.enabled = this.loadState();
    if (this.enabled) {
      this.sessionStart = Date.now();
      this.emitRaw({
        timestamp: new Date().toISOString(),
        category: 'SYSTEM',
        level: 'info',
        event: 'DEBUG_SESSION_START',
        data: { message: '调试模式已从存储恢复' },
        elapsed: 0,
      });
    }
  }

  get isEnabled(): boolean {
    return this.enabled;
  }

  enable(): void {
    if (this.enabled) return;
    this.enabled = true;
    this.sessionStart = Date.now();
    this.saveState(true);
    this.emitRaw({
      timestamp: new Date().toISOString(),
      category: 'SYSTEM',
      level: 'info',
      event: 'DEBUG_ENABLED',
      data: { message: '调试模式已开启，开始记录日志' },
      elapsed: 0,
    });
  }

  disable(): void {
    if (!this.enabled) return;
    this.emitRaw({
      timestamp: new Date().toISOString(),
      category: 'SYSTEM',
      level: 'info',
      event: 'DEBUG_DISABLED',
      data: { message: '调试模式已关闭，停止记录日志' },
      elapsed: Date.now() - this.sessionStart,
    });
    this.enabled = false;
    this.sessionStart = 0;
    this.saveState(false);
  }

  toggle(): boolean {
    if (this.enabled) {
      this.disable();
    } else {
      this.enable();
    }
    return this.enabled;
  }

  log(category: LogCategory, level: LogLevel, event: string, data?: unknown): void {
    if (!this.enabled) return;
    this.emitRaw({
      timestamp: new Date().toISOString(),
      category,
      level,
      event,
      data,
      elapsed: Date.now() - this.sessionStart,
    });
  }

  ai(event: string, data?: unknown): void { this.log('AI', 'info', event, data); }
  aiError(event: string, data?: unknown): void { logger.error(`AI:${event}`, data); this.log('AI', 'error', event, data); }
  tool(event: string, data?: unknown): void { this.log('TOOL', 'info', event, data); }
  toolError(event: string, data?: unknown): void { logger.error(`TOOL:${event}`, data); this.log('TOOL', 'error', event, data); }
  system(event: string, data?: unknown): void { this.log('SYSTEM', 'info', event, data); }
  systemWarn(event: string, data?: unknown): void { logger.warn(`SYSTEM:${event}`, data); this.log('SYSTEM', 'warn', event, data); }
  systemError(event: string, data?: unknown): void { logger.error(`SYSTEM:${event}`, data); this.log('SYSTEM', 'error', event, data); }
  user(event: string, data?: unknown): void { this.log('USER', 'info', event, data); }

  private emitRaw(entry: LogEntry): void {
    const prefix = `[${entry.category}] [+${entry.elapsed}ms]`;
    const style = this.getStyle(entry.category, entry.level);

    if (entry.data !== undefined) {
      console.groupCollapsed(
        `%c${prefix} %c${entry.event}`,
        style.label,
        style.event,
      );
      // eslint-disable-next-line no-console
      console.dir(entry.data);
      console.groupEnd();
    } else {
      // eslint-disable-next-line no-console
      console.log(
        `%c${prefix} %c${entry.event}`,
        style.label,
        style.event,
      );
    }
  }

  private getStyle(category: LogCategory, level: LogLevel) {
    const colors: Record<LogCategory, string> = {
      AI: '#1890ff',
      TOOL: '#722ed1',
      SYSTEM: '#52c41a',
      USER: '#fa8c16',
    };
    const weight = level === 'error' || level === 'warn' ? 'font-weight:bold;' : '';
    return {
      label: `color:${colors[category]};${weight}`,
      event: 'color:#333;font-weight:500;',
    };
  }

  private saveState(enabled: boolean): void {
    try {
      localStorage.setItem('AI_DEBUG_MODE', JSON.stringify(enabled));
    } catch { /* ignore */ }
  }

  private loadState(): boolean {
    try {
      const raw = localStorage.getItem('AI_DEBUG_MODE');
      if (raw !== null) return JSON.parse(raw) as boolean;
    } catch { /* ignore */ }
    return false;
  }
}

export const debugLogger = new DebugLogger();
